<i18n>
{
	"en": {
		"fromAddress": "From Address",
		"toAddress": "To Address",
		"eta": "Planned Arrival",
		"etd": "Planned Departure",
		"time": "Time",
		"missingDriver": "Missing driver",
		"driverNotContactable": "Driver missing contact info",
		"saveBeforeSendMessage": "Your unsaved changes will not be included unless you save first. Do you wish to save before sending the leg to Confirm?",
		"legSentToConfirm": "Leg sent to Confirm",
		"failedToSendLeg": "Could not send leg to Confirm",
		"noDriversInLeg": "Cannot send a leg without drivers to Confirm",
		"needToSaveTrip": "You need to save the trip at least once before anything can be sent to Confirm",
		"openBroadcastDrivers": "Open broadcast menu",
		"appSend": "Send leg to Confirm"
	},
	"no": {
		"fromAddress": "Fra adresse",
		"toAddress": "Til adresse",
		"eta": "Planlagt ankomst",
		"etd": "Planlagt avgang",
		"time": "Tid",
		"missingDriver": "Mangler sjåfør",
		"driverNotContactable": "Sjåfør mangler kontaktinfo",
		"saveBeforeSendMessage": "Dine endringer er ikke lagret og vil derfor ikke bli med om du ikke lagrer først. Vil du lagre før legen sendes til Confirm?",
		"legSentToConfirm": "Leg sendt til Confirm",
		"failedToSendLeg": "Kunne ikke sende leg til Confirm",
		"noDriversInLeg": "Kan ikke sende en leg uten sjåfører til Confirm",
		"needToSaveTrip": "Du må lagre turen minst én gang før noe kan sendes til Confirm",
		"openBroadcastDrivers": "Åpne kringkastningsmeny",
		"appSend": "Send leg til Confirm"
	}
}
</i18n>

<template>
	<t-card class="leg" border="default">
		<div slot="header" class="leg-header">
			<div class="leg-header-left">
				<span class="leg-header-title">Leg {{legNumber + 1}}</span>
				<div class="leg-header-arrow-buttons">
					<t-icon-button
						v-if="legNumber > 0"
						type="bp"
						icon="arrow-up"
						appearance="minimal"
						@click="moveLegUp"
					/>
					<t-icon-button
						v-if="legNumber < numOfLegs - 1"
						type="bp"
						icon="arrow-down"
						appearance="minimal"
						@click="moveLegDown"
					/>
				</div>
			</div>
			<t-icon-button
				:disabled="removeDisabled"
				appearance="minimal"
				intent="danger"
				icon="cross"
				type="bp"
				size="small"
				iconSize="xs"
				@click="removeLeg(legNumber)"
			/>
		</div>

		<div class="leg-content">
			<div class="grid-left">
				<TripAddressInput
					:title="$t('fromAddress')"
					:value="leg.fromAddress"
					:isInvalid="Boolean(errors.fromAddress)"
					:validationKey="errors.fromAddress"
					focusNextOnSelected
					@change="setLegFromAddress({ legNumber, ...$event})"
				/>

				<t-date-time-input
					:title="$t('etd')"
					:dateTimeObject="leg.etd"
					:isInvalid="Boolean(errors.etd)"
					:validationKey="errors.etd"
					@date-input="setLegEtd({ legNumber, dateValue: $event, hasTime: null })"
					@time-input="setLegEtd({ legNumber, timeValue: $event, hasTime: Boolean($event) })"
				/>

				<ActualTime
					field="atd"
					:dateTimeObject="leg.atd"
				/>

				<TripAddressInput
					:title="$t('toAddress')"
					:value="leg.toAddress"
					:isInvalid="Boolean(errors.toAddress)"
					:validationKey="errors.toAddress"
					focusNextOnSelected
					@change="setLegToAddress({ legNumber, ...$event})"
				/>

				<t-date-time-input
					:title="$t('eta')"
					:dateTimeObject="leg.eta"
					:isInvalid="Boolean(errors.eta)"
					:validationKey="errors.eta"
					@date-input="setLegEta({ legNumber, dateValue: $event, hasTime: null })"
					@time-input="setLegEta({ legNumber, timeValue: $event, hasTime: Boolean($event) })"
				/>

				<ActualTime
					field="ata"
					:dateTimeObject="leg.ata"
				/>

				<OperationalStatusFormInput
					style="grid-column: 3/3"
					:selectedItem="leg.status"
					:availableStatuses="selectableStatuses"
					showPopoverOnFocus
					@change="setLegStatus({legNumber, status: $event}) && focusNext()"
				/>
			</div>

			<div class="grid-right">
				<CargoCarriersInput
					ref="cargoCarriersInput"
					isInputForLeg
					:value="leg.cargoCarrier"
					@change="setLegCargoCarrier({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.cargoCarriersInput) })"
				/>
				<VendorInput
					ref="VendorsFormInput"
					:value="leg.vendor"
					:isInvalid="false"
					:validationKey="errors.vendor"
					@change="setLegVendor({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.VendorsFormInput) })"
				/>
				<DriverInput
					ref="DriversFormInput"
					:value="leg.driver"
					:needAccept="leg.needAccept"
					:isInvalid="Boolean(errors.driver)"
					:validationMessage="$t(errors.driver)"
					@change:needAccept="setLegNeedAccept({ legNumber, needAccept: $event })"
					@change="setLegDriver({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.DriversFormInput) })"
				/>
			</div>

			<div class="grid-bottom">
				<div v-if="leg.broadcastDrivers">
					<t-button
						:value="$t('openBroadcastDrivers')"
						type="bp"
						iconBefore="add"
						@click="broadcastDriversIsShow = true"
					/>
				</div>
				<t-button
					:value="$t('appSend')"
					appearance="primary"
					:intent="wasSent ? 'success' : 'default'"
					class="app-send-btn"
					type="bp"
					iconBefore="globe"
					@click="beforeSendLegToConfirm(trip.id)"
				/>
			</div>
		</div>

		<BroadcastDriversDialog
			v-if="leg.broadcastDrivers"
			:isShown="broadcastDriversIsShow"
			:broadcastDrivers="leg.broadcastDrivers"
			:errors="errors.broadcastDrivers || []"
			:wasBroadcasted="wasBroadcasted"
			:legNumber="legNumber"
			:legId="leg.id"
			@close="broadcastDriversIsShow = false"
		/>
		<ConfirmCancelDialog
			:isShown="showSaveBeforeSendDialog"
			:message="$t('saveBeforeSendMessage')"
			@cancel="showSaveBeforeSendDialog = false"
			@yes="saveBeforeSendToConfirm"
			@no="onSendLegToConfirm"
		/>
	</t-card>
</template>

<script>
// TODO: Validate leg vendor?

import CargoCarriersInput from '@/modules/cargoCarriers/CargoCarriersInput.vue';
import VendorInput from '@/modules/vendors/VendorInput.vue';
import DriverInput from '@/modules/drivers/DriverInput.vue';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput.vue';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';
import ActualTime from '../Time/ActualTime';
import ConfirmCancelDialog from '@/components/modals/ConfirmCancelDialog';
import BroadcastDriversDialog from '../broadcastDrivers/BroadcastDriversDialog';

import { mapState, mapMutations, mapActions } from '../store/newTrip.store.js';
import {
	statuses,
} from '@/core';
import TCard from '@/components/evergreen/layers/TCard';

export default {
	components: {
		TCard,
		CargoCarriersInput,
		VendorInput,
		DriverInput,
		OperationalStatusFormInput,
		TripAddressInput,
		ActualTime,
		ConfirmCancelDialog,
		BroadcastDriversDialog,
	},
	props: { leg: Object, numOfLegs: Number },
	data() {
		return {
			showAddressSearch: false,
			selectableStatuses: Object.values(statuses.trip),
			showSaveBeforeSendDialog: false,
			broadcastDriversIsShow: false,
		};
	},

	computed: {
		...mapState({
			trip: 'trip',
			dirty: 'dirty',
			errors(state) {
				return state.errors.legs && state.errors.legs[this.legNumber]
					? state.errors.legs[this.legNumber]
					: {};
			},
		}),
		legNumber() {
			return this.leg.legNumber;
		},
		removeDisabled() {
			return this.trip.legs.length < 2;
		},
		wasSent() {
			return this.leg.wasSent || this.trip.wasSent;
		},
		wasBroadcasted() {
			// TODO: Differentiate between broadcast and send.
			// Need to do this in Hub and DB.
			return this.wasSent;
		},
	},

	inject: ['saveTrip'], // Defined in newTrip.vue

	methods: {
		...mapMutations([
			'removeLeg',
			'swapLegOrder',
		]),
		...mapActions([
			'setLegStatus',
			'setLegFromAddress',
			'setLegToAddress',
			'setLegEtd',
			'setLegEta',
			'setLegCargoCarrier',
			'setLegVendor',
			'setLegNeedAccept',
			'setLegDriver',
			'setLegBroadcastDrivers',
			'sendLegToConfirm',
			'broadcastToDrivers',
		]),
		moveLegUp() {
			this.swapLegOrder({ legNumberBefore: this.legNumber, legNumberAfter: this.legNumber - 1 });
		},
		moveLegDown() {
			this.swapLegOrder({ legNumberBefore: this.legNumber, legNumberAfter: this.legNumber + 1 });
		},

		addBroadcastDriver(driver) {
			if (this.leg.broadcastDrivers.includes(driver)) {
				console.error(`Allready added ${driver.name}`);
			} else {
				this.setLegBroadcastDrivers({ legNumber: this.legNumber, broadcastDrivers: [...this.leg.broadcastDrivers, driver] });
			}
		},
		setBroadcastDrivers(broadcastDrivers) {
			this.setLegBroadcastDrivers({ legNumber: this.legNumber, broadcastDrivers });
		},

		getFirstInputElementByRef(ref) {
			return ref.$el.querySelector('input');
		},

		beforeSendLegToConfirm() {
			if (this.dirty) {
				this.showSaveBeforeSendDialog = true;
			} else {
				this.onSendLegToConfirm();
			}
		},

		async saveBeforeSendToConfirm() {
			this.showSaveBeforeSendDialog = false;
			let savedSuccessfully = await this.saveTrip({ validateDrivers: true });
			if (!savedSuccessfully) return;
			this.onSendLegToConfirm();
		},

		async onSendLegToConfirm() {
			this.showSaveBeforeSendDialog = false;
			this.$notify({ clean: true });
			try {
				await this.sendLegToConfirm(this.leg.id);
				this.$notify({
					type: 'success',
					title: this.$t('legSentToConfirm'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSendLeg'),
					duration: -1,
				});
			}
		},

		beforeBroadcastToDrivers() {
			if (this.dirty) {
				this.showSaveBeforeBroadcastDialog = true;
			} else {
				this.onBroadcastToDrivers();
			}
		},

		async saveBeforeBroadcastToDrivers() {
			this.showSaveBeforeSendDialog = false;
			let savedSuccessfully = await this.saveTrip({ validateDrivers: true });
			if (!savedSuccessfully) return;
			this.onBroadcastToDrivers();
		},

		async onBroadcastToDrivers() {
			this.showSaveBeforeSendDialog = false;
			this.$notify({ clean: true });
			try {
				await this.broadcastToDrivers(this.leg.id);
				this.$notify({
					type: 'success',
					title: this.$t('legBroadcastedToDrivers'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToBroadcastLeg'),
					duration: -1,
				});
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.leg {
	margin-bottom: 10px;
}

.leg-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: get-color($background, 'tint1');
	border-bottom: 1px solid #EDF0F2;
	padding: 2px 2px 2px 8px;
}

.leg-header-title {
	color: get-color($text, 'default');
	font-weight: 600;
	font-size: 12px;
	padding: 3px 0px;
}

.leg-header-left {
	display: grid;
	align-items: center;
	column-gap: 10px;
	grid-auto-flow: column;
	grid-template-columns: auto-fill;
}

.leg-header-arrow-buttons {
	display: flex;
}

.leg-content {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 700px 1fr;
	grid-template-areas:
		"left right"
		"bottom bottom";
	padding: 7px;
}

.grid-left {
	display: grid;
	gap: 20px;
	grid-template-columns: 	minmax(min-content, 1fr)
							repeat(2, minmax(min-content, max-content));
	grid-template-rows: auto auto 1fr;
	grid-area: left;
}

.grid-right {
	display: grid;
	gap: 20px;
	grid-area: right;
	grid-template-rows: repeat(2, minmax(min-content, max-content));

	border-left: 1px solid get-color($border, 'default');
	margin-left: 20px;
	padding-left: 19px;
}

.grid-bottom {
	grid-area: bottom;
	display: flex;
	flex-wrap: wrap;
	align-items: center;

	&>* {
		margin-left: 20px;
	}
	&>*:first-child {
		margin-left: 0px;
	}
}

.app-send-btn {
	justify-self: flex-end;
	margin-left: auto;
}
</style>